import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, TextField, MenuItem, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert, InputAdornment, IconButton, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AcessoNegado from '../components/AcessoNegado';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const STATUS_INFO = {
  ativo:     { label: 'Ativo',     color: 'success' },
  desligado: { label: 'Desligado', color: 'default' },
};

const ESTADO_CIVIL_OPCOES = [
  { value: 'solteiro',      label: 'Solteiro(a)' },
  { value: 'casado',        label: 'Casado(a)' },
  { value: 'divorciado',    label: 'Divorciado(a)' },
  { value: 'viuvo',         label: 'Viúvo(a)' },
  { value: 'uniao_estavel', label: 'União estável' },
];

const TIPO_CONTRATACAO_OPCOES = [
  { value: 'clt',     label: 'CLT' },
  { value: 'pj',      label: 'PJ' },
  { value: 'estagio', label: 'Estágio' },
  { value: 'outro',   label: 'Outro' },
];

const FORM_VAZIO = {
  cpf: '', rg: '', data_nascimento: '', estado_civil: '', endereco: '', telefone: '',
  email_pessoal: '', contato_emergencia_nome: '', contato_emergencia_telefone: '',
  cargo: '', tipo_contratacao: '', data_admissao: '', data_demissao: '', jornada_trabalho: '',
};

// Proteção temporária: até corrigirmos na origem (backend), tira um "— None"
// que às vezes vem grudado no nome quando algum dado ainda não foi preenchido.
const limparNome = (nome) => (nome || '').replace(/\s*—\s*None\s*$/i, '').trim();

function TituloSecao({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
      <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: '#e94560' }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function Rh() {
  const { temAcesso } = useAuth();
  const acessoLiberado = temAcesso('rh');

  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('ativo');

  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [somenteLeitura, setSomenteLeitura] = useState(false);

  const carregar = () => {
    setLoading(true);
    api.get('/fichas-cadastrais/')
      .then((res) => setFichas(res.data))
      .catch(() => setErro('Não foi possível carregar as fichas.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!acessoLiberado) { setLoading(false); return; }
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acessoLiberado]);

    const preencherForm = (ficha) => {
    setForm({
      cpf: ficha.cpf || '',
      rg: ficha.rg || '',
      data_nascimento: ficha.data_nascimento || '',
      estado_civil: ficha.estado_civil || '',
      endereco: ficha.endereco || '',
      telefone: ficha.telefone || '',
      email_pessoal: ficha.email_pessoal || '',
      contato_emergencia_nome: ficha.contato_emergencia_nome || '',
      contato_emergencia_telefone: ficha.contato_emergencia_telefone || '',
      cargo: ficha.cargo || '',
      tipo_contratacao: ficha.tipo_contratacao || '',
      data_admissao: ficha.data_admissao || '',
      data_demissao: ficha.data_demissao || '',
      jornada_trabalho: ficha.jornada_trabalho || '',
    });
  };

  const abrirEdicao = (ficha) => {
    preencherForm(ficha);
    setErro('');
    setSomenteLeitura(false);
    setEditando(ficha);
  };

  const abrirVisualizacao = (ficha) => {
    preencherForm(ficha);
    setErro('');
    setSomenteLeitura(true);
    setEditando(ficha);
  };

  const fecharEdicao = () => {
    setEditando(null);
    setSomenteLeitura(false);
  };

    const salvar = async () => {
    setSalvando(true);
    setErro('');
    const payload = {
      ...form,
      data_nascimento: form.data_nascimento || null,
      data_admissao: form.data_admissao || null,
      data_demissao: form.data_demissao || null,
    };
    try {
      await api.patch(`/fichas-cadastrais/${editando.id}/`, payload);
      fecharEdicao();
      carregar();
    } catch (e) {
      console.error('Erro ao salvar ficha:', e.response?.data || e);
      setErro('Não foi possível salvar. Confira os campos e tente de novo.');
    } finally {
      setSalvando(false);
    }
  };

  if (!acessoLiberado) {
    return (
      <AcessoNegado mensagem="Você não tem acesso ao RH. Fale com o Admin se precisar desse acesso." />
    );
  }

  const fichasFiltradas = fichas.filter((f) => {
    const statusReal = f.pendente_preenchimento ? 'pendente' : f.status;
    if (filtroStatus !== 'todos' && statusReal !== filtroStatus) return false;
    if (busca && !f.membro_equipe_nome.toLowerCase().includes(busca.toLowerCase())) return false;
    return true;
  });

  return (
    <Box>
      <PageHeader titulo="RH" />

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            select label="Status" value={filtroStatus} size="small"
            onChange={(e) => setFiltroStatus(e.target.value)}
            sx={{ width: 160 }}
          >
            <MenuItem value="ativo">Ativos</MenuItem>
            <MenuItem value="desligado">Desligados</MenuItem>
            <MenuItem value="pendente">Pendentes</MenuItem>
            <MenuItem value="todos">Todos</MenuItem>
          </TextField>
          <TextField
            label="Buscar por nome" size="small" value={busca}
            onChange={(e) => setBusca(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              },
            }}
            sx={{ flexGrow: 1, minWidth: 220 }}
          />
        </CardContent>
      </Card>

      {erro && !editando && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {loading ? (
        <Carregando />
      ) : (
        <Card>
          <CardContent>
            {fichasFiltradas.length === 0 ? (
              <Alert severity="info">Nenhuma ficha encontrada para esse filtro.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Nome</TableCell>
                    <TableCell>Cargo</TableCell>
                    <TableCell align="center">Admissão</TableCell>
                    <TableCell align="center">Demissão</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fichasFiltradas.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{limparNome(f.membro_equipe_nome)}</TableCell>
                      <TableCell>{f.cargo || '—'}</TableCell>
                      <TableCell align="center">
                        {f.data_admissao ? dayjs(f.data_admissao).format('DD/MM/YYYY') : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {f.data_demissao ? dayjs(f.data_demissao).format('DD/MM/YYYY') : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {f.pendente_preenchimento ? (
                          <Chip label="Pendente" size="small" />
                        ) : (
                          <Chip
                            label={(STATUS_INFO[f.status] || STATUS_INFO.ativo).label}
                            color={(STATUS_INFO[f.status] || STATUS_INFO.ativo).color}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => abrirVisualizacao(f)} title="Visualizar">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => abrirEdicao(f)} title="Editar">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editando} onClose={fecharEdicao} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3 }}>
                {limparNome(editando?.membro_equipe_nome)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {somenteLeitura ? 'Ficha cadastral — somente visualização' : 'Ficha cadastral'}
              </Typography>
            </Box>
            {editando && (
              editando.pendente_preenchimento ? (
                <Chip label="Pendente" size="small" />
              ) : (
                <Chip
                  label={(STATUS_INFO[editando.status] || STATUS_INFO.ativo).label}
                  color={(STATUS_INFO[editando.status] || STATUS_INFO.ativo).color}
                  size="small"
                />
              )
            )}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ backgroundColor: '#fafafa', pt: 3 }}>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'white', border: '1px solid #eee', mb: 2.5 }}>
            <TituloSecao>Dados pessoais</TituloSecao>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CPF" fullWidth size="small" value={form.cpf} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="RG" fullWidth size="small" value={form.rg} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, rg: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de nascimento" type="date" fullWidth size="small" disabled={somenteLeitura}
                  InputLabelProps={{ shrink: true }} value={form.data_nascimento}
                  sx={{ minWidth: 190 }}
                  onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select label="Estado civil" fullWidth size="small" value={form.estado_civil} disabled={somenteLeitura}
                  sx={{ minWidth: 190 }}
                  onChange={(e) => setForm({ ...form, estado_civil: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  {ESTADO_CIVIL_OPCOES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Endereço" fullWidth size="small" value={form.endereco} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Telefone" fullWidth size="small" value={form.telefone} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail pessoal" fullWidth size="small" value={form.email_pessoal} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, email_pessoal: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contato de emergência — nome" fullWidth size="small" disabled={somenteLeitura}
                  value={form.contato_emergencia_nome}
                  onChange={(e) => setForm({ ...form, contato_emergencia_nome: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Contato de emergência — telefone" fullWidth size="small" disabled={somenteLeitura}
                  value={form.contato_emergencia_telefone}
                  onChange={(e) => setForm({ ...form, contato_emergencia_telefone: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'white', border: '1px solid #eee' }}>
            <TituloSecao>Dados contratuais</TituloSecao>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cargo" fullWidth size="small" value={form.cargo} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select label="Tipo de contratação" fullWidth size="small" value={form.tipo_contratacao} disabled={somenteLeitura}
                  sx={{ minWidth: 190 }}
                  onChange={(e) => setForm({ ...form, tipo_contratacao: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  {TIPO_CONTRATACAO_OPCOES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de admissão" type="date" fullWidth size="small" disabled={somenteLeitura}
                  InputLabelProps={{ shrink: true }} value={form.data_admissao}
                  sx={{ minWidth: 190 }}
                  onChange={(e) => setForm({ ...form, data_admissao: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Data de demissão" type="date" fullWidth size="small" disabled={somenteLeitura}
                  InputLabelProps={{ shrink: true }} value={form.data_demissao}
                  sx={{ minWidth: 190 }}
                  onChange={(e) => setForm({ ...form, data_demissao: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Jornada de trabalho" fullWidth size="small" value={form.jornada_trabalho} disabled={somenteLeitura}
                  onChange={(e) => setForm({ ...form, jornada_trabalho: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {somenteLeitura ? (
            <Button onClick={fecharEdicao}>Fechar</Button>
          ) : (
            <>
              <Button onClick={fecharEdicao}>Cancelar</Button>
              <Button
                variant="contained" onClick={salvar} disabled={salvando}
                sx={{ backgroundColor: '#1a1a2e', '&:hover': { backgroundColor: '#e94560' } }}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}